<template>
    <div :class="navClasses" class="pagination-wrap">
        <nav v-if="hasPages" class="pagination" :aria-label="translate('pagination.nav-label')">
            <!-- previous -->
            <span
                v-if="onFirstPage"
                class="pagination-button-arrow disabled"
                aria-disabled="true"
                :aria-label="translate('pagination.previous')"
            >
                <svg
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="8"
                    height="14"
                    fill="none"
                    viewBox="0 0 8 14"
                    class="pagination-b"
                >
                    <path
                        fill="#135958"
                        fill-rule="evenodd"
                        d="M.315 5.948l-.01.01c-.407.39-.407 1.024 0 1.415l5.91 5.664c.409.391 1.07.391 1.478 0 .408-.39.408-1.025 0-1.416L2.522 6.665 7.694 1.71c.408-.39.408-1.025 0-1.416-.408-.39-1.07-.39-1.478 0l-5.9 5.655z"
                        clip-rule="evenodd"
                    />
                </svg>
            </span>
            <a
                v-else
                :href="processURL(prevPageUrl)"
                rel="prev"
                :aria-label="translate('pagination.previous')"
                class="pagination-button-arrow"
            >
                <svg
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="8"
                    height="14"
                    fill="none"
                    viewBox="0 0 8 14"
                    class="pagination-b"
                >
                    <path
                        fill="#135958"
                        fill-rule="evenodd"
                        d="M.315 5.948l-.01.01c-.407.39-.407 1.024 0 1.415l5.91 5.664c.409.391 1.07.391 1.478 0 .408-.39.408-1.025 0-1.416L2.522 6.665 7.694 1.71c.408-.39.408-1.025 0-1.416-.408-.39-1.07-.39-1.478 0l-5.9 5.655z"
                        clip-rule="evenodd"
                    />
                </svg>
            </a>
            <!-- end previous -->
            <div class="numeration-container">
                <div class="scrollable">
                    <!-- numeration -->
                    <template v-for="link in links" :key="link.label">
                        <span v-if="isDotsLink(link)" class="pagination-button-number disabled" aria-disabled="true"
                            >...</span
                        >
                        <span
                            v-else-if="isLinkCurrentPage(link)"
                            class="pagination-button-number active"
                            aria-current="page"
                        >
                            {{ link.label }}
                        </span>
                        <a
                            v-else
                            :href="processURL(link.url)"
                            class="pagination-button-number"
                            :aria-label="translate('pagination.go-to-page', link.label)"
                            >{{ link.label }}</a
                        >
                    </template>
                    <!-- end numeration -->
                </div>
            </div>
            <!-- next -->
            <a
                v-if="hasMorePages"
                :href="processURL(nextPageUrl)"
                rel="next"
                :aria-label="translate('pagination.next')"
                class="pagination-button-arrow"
            >
                <svg
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="8"
                    height="14"
                    fill="none"
                    viewBox="0 0 8 14"
                    class="pagination-f"
                >
                    <path
                        fill="#135958"
                        fill-rule="evenodd"
                        d="M7.684 5.948l.01.01c.408.39.408 1.024 0 1.415l-5.91 5.664c-.409.391-1.07.391-1.478 0-.408-.39-.408-1.025 0-1.416l5.171-4.956L.306 1.71C-.102 1.32-.102.684.306.293c.408-.39 1.07-.39 1.477 0l5.9 5.655z"
                        clip-rule="evenodd"
                    />
                </svg>
            </a>
            <span
                v-else
                class="pagination-button-arrow disabled"
                aria-disabled="true"
                :aria-label="translate('pagination.next')"
            >
                <svg
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="8"
                    height="14"
                    fill="none"
                    viewBox="0 0 8 14"
                    class="pagination-f"
                >
                    <path
                        fill="#135958"
                        fill-rule="evenodd"
                        d="M7.684 5.948l.01.01c.408.39.408 1.024 0 1.415l-5.91 5.664c-.409.391-1.07.391-1.478 0-.408-.39-.408-1.025 0-1.416l5.171-4.956L.306 1.71C-.102 1.32-.102.684.306.293c.408-.39 1.07-.39 1.477 0l5.9 5.655z"
                        clip-rule="evenodd"
                    />
                </svg>
            </span>
            <!-- end next -->
        </nav>
        <!-- pagination summary -->
        <div class="tw-text-grey tw-text-sm tw-text-center" :class="{ 'tw-mt-4': hasPages }">
            <span>{{ t('pagination.results', totalItems) }}</span>
        </div>
    </div>
</template>

<script>
import { computed } from 'vue';
import i18n from '@/js/vue/mixins/i18n';

export default {
    name: 'Pagination',
    mixins: [i18n],
    inject: ['lang', 'translations'],
    props: {
        // laravel's pagination
        paginator: {
            type: Object,
            required: true,
        },
        navClasses: {
            type: String,
            required: false,
            default: '',
        },
        relative: {
            type: Boolean,
            default: false,
        },
        useCurrentPath: {
            type: Boolean,
            default: false,
        },
    },
    setup(props) {
        /*
     | ----------------
     | Computed getters
     | ----------------
     */

        // Get the current page number
        const currentPage = computed(() => props.paginator.current_page);

        // Get the last page number
        const lastPage = computed(() => props.paginator.last_page);

        // Get the URL for the first page
        const firstPageUrl = computed(() => props.paginator.first_page_url);

        // Get the URL for the next page
        const nextPageUrl = computed(() => props.paginator.next_page_url);

        // Get the URL for the previous page
        const prevPageUrl = computed(() => props.paginator.prev_page_url);

        // Get the URL for the last page
        const lastPageUrl = computed(() => props.paginator.last_page_url);

        // Get the paginator links as an array
        const Links = computed(() => props.paginator.links);

        // Get the paginator links as an array, without prev/next links - first and last element in the array
        const links = computed(() => Links.value.slice(1, -1));

        // Get the Previous link - never null/empty (first element in the array)
        const prevLink = computed(() => Links.value[0]);

        // Get the Next link - never null/empty (last element in the array)
        const nextLink = computed(() => Links.value[Links.value.length - 1]);

        // Get the base path for paginator generated URLs
        const path = computed(() => props.paginator.path);

        // The number of items to be shown per page
        const perPage = computed(() => props.paginator.per_page);

        // Get the result number of the first item in the results (first item in the slice)
        const from = computed(() => props.paginator.from);

        // Get the result number of the last item in the results (last number in the slice)
        const to = computed(() => props.paginator.to);

        // Get the total number of items being paginated
        const totalItems = computed(() => props.paginator.total);

        // Get the items for the current page
        const items = computed(() => {
            const items = [];
            for (let i = from.value; i <= to.value; i++) {
                items.push(i);
            }
            return items;
        });

        // Get the actual number of items for the current page
        const count = computed(() => to.value - from.value + 1); // or items.length

        // Determine if there are more items in the data store
        const hasMorePages = computed(() => currentPage.value < lastPage.value);

        // Determine if there are enough items to split into multiple pages
        // computed(() => currentPage.value !== 1 || hasMorePages.value);
        const hasPages = computed(() => lastPage.value > 1);

        // Determine if the paginator is on the first page
        const onFirstPage = computed(() => currentPage.value <= 1);

        // Determine if link is "dotted"
        const isDotsLink = (link) => link.url === null || link.label === '...';

        // Check if the link is for the current page
        const isLinkCurrentPage = (link) => +link.label === currentPage.value;

        // const currentPath = () =>

        function processURL(url) {
            if (props.relative) {
                url = new URL(url);
                url = url.pathname + url.search;
            }

            if (props.useCurrentPath) {
                url = new URL(url);
                url = url.protocol + '//' + url.host + location.pathname + url.search;
            }
            return url;
        }

        return {
            currentPage,
            lastPage,
            firstPageUrl,
            nextPageUrl,
            prevPageUrl,
            lastPageUrl,
            links,
            Links,
            path,
            perPage,
            from,
            to,
            totalItems,
            items,
            count,
            hasMorePages,
            hasPages,
            onFirstPage,
            prevLink,
            nextLink,
            isLinkCurrentPage,
            isDotsLink,
            processURL,
        };
    },
};
</script>
<style scoped lang="scss">
@import '@/sass/_vars';

.pagination {
    width: 100%;
    max-width: 450px;
    font-size: $fs-heading-4;
    line-height: $lh-heading-4;
    font-weight: $fw-medium;
    color: $dark-green;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 auto;
}
.pagination-wrap > .summary {
    color: $grey;
}
.pagination a {
    text-decoration: none;
    color: $dark-green;
}
.numeration-container {
    min-width: 0;
    .scrollable {
        display: flex;
        flex-direction: row;
        align-items: center;
        overflow: hidden;
        margin: 0 5px;
        overflow-x: auto;
        /* hide scrollbar */
        -ms-overflow-style: none; // IE 11 and Edge
        scrollbar-width: none; // Firefox
        &::-webkit-scrollbar {
            display: none; // Chrome, Safari and Opera
        }
    }
}

.pagination-button-arrow {
    padding: 12px 16px;
    margin: 0;
    border: solid 1px $dark-green;
    cursor: pointer;
    border-radius: $rd-small;
    > svg {
        display: block;
    }
    &:hover {
        path,
        rect {
            fill: lighten($dark-green, 10%);
        }
    }
    &.disabled {
        cursor: auto;
        pointer-events: none;
        border: solid 1px $grey;
        .pagination-f,
        .pagination-b {
            path,
            rect {
                fill: $grey;
            }
        }
    }
    .pagination-f,
    .pagination-b {
        path,
        rect {
            transition: 0.5s;
            fill: $dark-green;
        }
    }
}

.pagination-button-number {
    padding: 8px 12px;
    margin: 0 4px;
    border: 1px solid $light-grey2;
    font-size: $fs-paragraph;
    line-height: $lh-paragraph;
    line-height: 1;
    border-radius: $rd-small;
    cursor: pointer;
    transition: 0.5s;
    @media screen and (max-width: 320px) {
        font-size: $fs-paragraph-small;
        line-height: $lh-paragraph-small;
        padding: 8px 10px;
    }
    &:hover {
        border: 1px solid $grey;
        color: lighten($dark-green, 10%);
    }
    &.active {
        background-color: $dark-green;
        border: 1px solid $dark-green;
        color: $white;
        font-weight: $fw-medium;
        cursor: auto;
        pointer-events: none;
    }
    &.active:hover {
        background-color: $dark-green;
        color: $white;
        font-weight: $fw-medium;
    }
    &.disabled {
        color: $grey;
        cursor: auto;
        pointer-events: none;
    }
}
</style>
